import { Box, Button, Grid2, Stack } from '@mui/material'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Fade } from 'utoniq-ui'

import {
  GachaItemDetailFragment,
  GachaOrderDetailFragment,
  GachaRewardItem,
  UserItemDetailFragment,
  UserItemSerial,
} from '@/clients/utoniq-core/schema'
import { constants } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

import { ButtonSetCell, State } from '..'
import { ItemCell } from './ItemCell'

export type EmphasisRarityId =
  | 'common'
  | 'rare'
  | 'superrare'
  | 'ultrarare'
  | 'limited'
export type RewardItemWithFistTimeObtainedRare = Pick<
  GachaRewardItem,
  'count' | 'itemId'
> & {
  userItem: UserItemDetailFragment
  item: GachaItemDetailFragment
  obtainedUserItemSerials: Array<
    Pick<UserItemSerial, 'itemId' | 'formattedSerialNumber'>
  >
  fistTimeObtainedRareItem?: boolean
}

export type TenGachaResultCellProps = {
  loading: boolean
  ticket: State['ticket']
  artistId?: string
  rewardItems: GachaOrderDetailFragment['rewardItems']
  skipAnimation: boolean
  gacha?: GachaOrderDetailFragment['gacha']
  rollGacha: (count: number) => void
  onSelectUserItem: (itemId: string) => void
  setSkipDisplayAnimation: Dispatch<SetStateAction<boolean>>
  isPointOnly: boolean
  prepaidPointBalance: number
}
/**
 * @description
 * 10連ガチャ結果の画像表示エリアコンポーネント
 * 3カラム表示で、1行目：3個、2行目：4個、3行目：3個のアイテムを表示する。
 */
export const TenGachaResultCell: Cell<TenGachaResultCellProps> = ({
  loading,
  ticket,
  artistId,
  rewardItems,
  skipAnimation,
  gacha,
  rollGacha,
  onSelectUserItem,
  setSkipDisplayAnimation,
  isPointOnly,
  prepaidPointBalance,
}) => {
  const { router } = useRouter()
  const { t } = useTranslation('gachaResult')

  const [allItemAppeared, setAllItemAppeared] = useState(false)
  const [animationComplete, setAnimationComplete] = useState<boolean[]>(
    [...new Array(10)].map(() => false)
  )
  const [completeAllAnimation, setCompleteAllAnimation] = useState(false)

  /**
   * @description
   * 強調表現をするレアリティIDをセットしている。
   * どのレアリティまで強調表現するかはまだ未確定なので、現在はここで静的に入れている。
   */
  const emphasis: EmphasisRarityId[] = [
    'rare',
    'superrare',
    'ultrarare',
    'limited',
  ] as unknown as EmphasisRarityId[]

  const rewardItemsWithFistTimeObtainedRareFlag =
    setFistTimeObtainedRareItemFlag(rewardItems, emphasis)

  const commonItemAnimationProps = {
    emphasis,
    allItemAppeared,
    skipAnimation,
    setAllItemAppeared,
    setAnimationComplete,
    setCompleteAllAnimation,
  }

  useEffect(() => {
    // 既に取得したガチャの場合アニメーションをスキップする
    const completedGachas = JSON.parse(
      sessionStorage.getItem(constants.storageKeys.COMPLETED_GACHAS) || '[]'
    )
    if (completedGachas.includes(router.pathname)) {
      setSkipDisplayAnimation(true)
      return
    }
    sessionStorage.setItem(
      constants.storageKeys.COMPLETED_GACHAS,
      JSON.stringify([...completedGachas, router.pathname])
    )
  }, [])

  return (
    <CellFrame id="result-cell" sx={{ py: 6, px: 5 }}>
      <Box sx={{ height: 480, position: 'relative' }}>
        {/* 1行目： ガチャ結果アイテムの1~3個目を表示 */}
        <Box id="first-column" sx={{ py: 2 }}>
          <Grid2
            container
            direction="row"
            justifyContent="center"
            alignItems="flex-start"
            columnSpacing={4}
          >
            {rewardItemsWithFistTimeObtainedRareFlag.map(
              (rewardItem, index) => {
                if (index > 2) return

                return (
                  <ItemCell
                    key={index}
                    rewardItem={rewardItem}
                    rewardItemOrder={index}
                    onClick={() => onSelectUserItem(rewardItem.itemId)}
                    animation={{
                      ...commonItemAnimationProps,
                      startFlip:
                        index === 0 ? true : animationComplete[index - 1],
                      fistTimeObtainedRareEffect:
                        !skipAnimation && rewardItem.fistTimeObtainedRareItem,
                    }}
                  />
                )
              }
            )}
          </Grid2>
        </Box>

        {/* 2行目： ガチャ結果アイテムの4~7個目を表示 */}
        <Box id="second-column" sx={{ py: 2 }}>
          <Grid2
            container
            direction="row"
            justifyContent="center"
            alignItems="flex-start"
            flexWrap="nowrap"
            columnSpacing={4}
          >
            {rewardItemsWithFistTimeObtainedRareFlag.map(
              (rewardItem, index) => {
                if (index < 3 || index > 6) return

                return (
                  <ItemCell
                    key={index}
                    rewardItem={rewardItem}
                    rewardItemOrder={index}
                    onClick={() => onSelectUserItem(rewardItem.itemId)}
                    animation={{
                      ...commonItemAnimationProps,
                      startFlip: animationComplete[index - 1],
                      fistTimeObtainedRareEffect:
                        !skipAnimation && rewardItem.fistTimeObtainedRareItem,
                    }}
                  />
                )
              }
            )}
          </Grid2>
        </Box>

        {/* 3行目： ガチャ結果アイテムの8~10個目を表示 */}
        <Box id="third-column" sx={{ py: 2 }}>
          <Grid2
            container
            direction="row"
            justifyContent="center"
            alignItems="flex-start"
            columnSpacing={4}
          >
            {rewardItemsWithFistTimeObtainedRareFlag.map(
              (rewardItem, index) => {
                if (index < 7) return

                return (
                  <ItemCell
                    key={index}
                    rewardItem={rewardItem}
                    rewardItemOrder={index}
                    onClick={() => onSelectUserItem(rewardItem.itemId)}
                    animation={{
                      ...commonItemAnimationProps,
                      startFlip: animationComplete[index - 1],
                      fistTimeObtainedRareEffect:
                        !skipAnimation && rewardItem.fistTimeObtainedRareItem,
                    }}
                  />
                )
              }
            )}
          </Grid2>
        </Box>
      </Box>

      {/* アニメーションスキップボタン、スキップしていないアニメーション中はのみ表示  */}
      {!skipAnimation && !completeAllAnimation && (
        <Stack>
          <Button
            variant="text"
            sx={{ ml: 'auto', maxWidth: 144 }}
            onClick={() => setSkipDisplayAnimation(true)}
          >
            {t('skip')}
          </Button>
        </Stack>
      )}

      {/* もう一度回すボタン、アニメーションスキップ後またはアニメーション完了後表示  */}
      {(skipAnimation || completeAllAnimation) && (
        <Stack flex={1} flexDirection="column" justifyContent="end">
          <Fade>
            <ButtonSetCell
              loading={loading}
              ticket={ticket}
              count={10}
              rollGacha={rollGacha}
              artistId={artistId}
              remainingLimitedItemCount={gacha?.remainingLimitedItemCount ?? 0}
              totalLimitedItemCount={gacha?.totalLimitedItemCount ?? 0}
              isLimitedGacha={!!gacha?.isLimited}
              price={gacha?.price ?? 0}
              isPointOnly={isPointOnly}
              prepaidPointBalance={prepaidPointBalance}
            />
          </Fade>
        </Stack>
      )}
    </CellFrame>
  )
}

/**
 * @description
 * 以下条件を全て満たす時、新規レアリティアイテム特殊演出用のフラグを付与する
 *
 * - レアリティがスーパーレアの場合
 * - 新規取得アイテムの場合
 * - ダブった場合、1枚目のアイテムだった場合
 */
const setFistTimeObtainedRareItemFlag = (
  rewardItems: GachaOrderDetailFragment['rewardItems'],
  emphasis: EmphasisRarityId[]
): RewardItemWithFistTimeObtainedRare[] => {
  return rewardItems.map((rewardItem, index) => {
    // スーパーレア・ウルトラレアかどうか
    if (!emphasis.includes(rewardItem.item?.rarity?.id as EmphasisRarityId)) {
      return rewardItem
    }

    // 新規取得アイテムかどうか
    if (rewardItem?.count !== rewardItem?.userItem?.number) return rewardItem

    // 今回のガチャでアイテムがダブっている場合、表示が1枚目かどうか
    if (
      rewardItems.some(
        rewardItem => rewardItem?.itemId === rewardItems[index]?.itemId
      ) &&
      rewardItems
        .slice(0, index)
        .some(rewardItem => rewardItem.itemId === rewardItems[index]?.itemId)
    )
      return rewardItem

    return { ...rewardItem, fistTimeObtainedRareItem: true }
  })
}
